'use client'

import { StatsCard } from '@/components/ui/stats-card'
import { Badge } from '@/components/ui/badge'
import { Server, Globe, Wifi, WifiOff, CheckCircle, XCircle, Zap, Network, Shuffle, Router, Globe2 } from 'lucide-react'

interface ServerData {
  id: string
  region?: string
  status?: string
  flags?: string[]
  isActive?: boolean
  ipv4?: string
  ipv6?: string
}

interface ServerStatsCardsProps {
  servers: ServerData[]
}

export function ServerStatsCards({ servers }: ServerStatsCardsProps) {
  // Filter active servers only
  const activeServers = servers.filter(server => server.isActive !== false)
  
  // Calculate statistics
  const totalServers = activeServers.length
  const onlineServers = activeServers.filter(server => server.status === 'online').length
  const offlineServers = activeServers.filter(server => server.status === 'offline').length
  const unknownServers = activeServers.filter(server => server.status === 'unknown').length
  const dnsRrServers = activeServers.filter(server => server.flags?.includes('RR')).length
  const anycastServers = activeServers.filter(server => 
    server.flags?.includes('Anycast') || server.flags?.includes('IX')
  ).length
  const ipv4Servers = activeServers.filter(server => server.ipv4 && server.ipv4.trim() !== '').length
  const ipv6Servers = activeServers.filter(server => server.ipv6 && server.ipv6.trim() !== '').length

  const onlinePercentage = totalServers > 0 ? Math.round((onlineServers / totalServers) * 100) : 0

  const statsCards = [
    {
      title: 'Total Servers',
      value: totalServers,
      icon: Server,
      color: 'blue' as const,
      description: 'Active servers'
    },
    {
      title: 'Online Status',
      value: onlineServers,
      icon: CheckCircle,
      color: 'green' as const,
      description: `${onlinePercentage}% uptime`,
      badge: offlineServers > 0 ? `${offlineServers} offline` : undefined,
      badgeVariant: offlineServers > 0 ? 'destructive' as const : undefined
    },
    {
      title: 'Round Robin',
      value: dnsRrServers,
      icon: Shuffle,
      color: 'purple' as const,
      description: 'DNS round-robin servers'
    },
    {
      title: 'IP Protocols',
      value: ipv4Servers,
      icon: Globe2,
      color: 'cyan' as const,
      description: 'IPv4 servers',
      badge: ipv6Servers > 0 ? `${ipv6Servers} IPv6` : undefined,
      badgeVariant: 'secondary' as const
    },
    {
      title: 'Anycast',
      value: anycastServers,
      icon: Network,
      color: 'orange' as const,
      description: 'Anycast & IX servers'
    }
  ]

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {statsCards.map((stat, index) => (
        <div key={index} className="relative">
          <StatsCard
            title={stat.title}
            value={stat.value}
            description={stat.description}
            icon={stat.icon}
            color={stat.color}
          />
          {stat.badge && (
            <div className="absolute bottom-2 right-4">
              <Badge variant={stat.badgeVariant} className="text-xs">
                {stat.badge}
              </Badge>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}